import {ReactNode, useRef, useState} from 'react';

// Small dependency-free SVG charts for the Team Activity screens.
// Interactive (hover tooltips + highlights) and animated on mount;
// all motion is disabled under prefers-reduced-motion.

export const ChartStyles = () => (
  <style>{`
    @keyframes ta-grow-y { from { transform: scaleY(0); } to { transform: scaleY(1); } }
    @keyframes ta-grow-x { from { transform: scaleX(0); } to { transform: scaleX(1); } }
    @keyframes ta-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
    @keyframes ta-fadeup { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
    .ta-bar {
      transform-box: fill-box;
      transform-origin: center bottom;
      animation: ta-grow-y .5s cubic-bezier(.2,.7,.3,1) backwards;
    }
    .ta-hbar {
      transform-box: fill-box;
      transform-origin: left center;
      animation: ta-grow-x .55s cubic-bezier(.2,.7,.3,1) backwards;
    }
    .ta-line { stroke-dasharray: 1; animation: ta-draw .7s ease-out backwards; }
    .ta-fadeup { animation: ta-fadeup .4s ease-out backwards; }
    .ta-mixseg {
      transform-origin: left center;
      animation: ta-grow-x .5s cubic-bezier(.2,.7,.3,1) backwards;
    }
    .ta-tt {
      position: absolute; z-index: 20; pointer-events: none;
      opacity: 0; transform: translate(-50%, 6px);
      transition: opacity .12s ease, transform .12s ease;
      background: #fff; border: 1px solid #D4CCE0; border-radius: 10px;
      box-shadow: 0 12px 32px rgba(32,26,44,.16);
      padding: 9px 11px; min-width: 150px; font-size: 12px; color: #201A2C;
    }
    .ta-tt.on { opacity: 1; transform: translate(-50%, 0); }
    .ta-row { transition: background-color .12s ease; }
    .ta-legend-btn { transition: background-color .12s ease, opacity .12s ease; }
    .ta-legend-btn:hover { background: #F5F2F9; }
    .ta-card { transition: transform .15s ease, box-shadow .15s ease; }
    .ta-card:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(32,26,44,.09); }
    @media (prefers-reduced-motion: reduce) {
      .ta-bar, .ta-hbar, .ta-line, .ta-fadeup, .ta-mixseg { animation: none; }
      .ta-tt, .ta-row, .ta-card { transition: none; }
      .ta-card:hover { transform: none; }
    }
  `}</style>
);

export const Sparkline = ({
  values,
  color = '#AE3EC9',
  width = 90,
  height = 26,
}: {
  values: number[];
  color?: string;
  width?: number;
  height?: number;
}) => {
  if (!values.length) return null;
  const p = 2;
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? (width - p * 2) / (values.length - 1) : 0;
  const pts = values.map((v, i) => [p + i * step, height - p - (v / max) * (height - p * 2)]);
  const line = pts.map((pt, i) => `${i ? 'L' : 'M'}${pt[0].toFixed(1)} ${pt[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{display: 'block'}} aria-hidden="true">
      <path
        className="ta-line"
        d={line}
        pathLength={1}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill={color} />
    </svg>
  );
};

interface TooltipState {
  leftPct: number;
  topPx: number;
  content: ReactNode;
}

const Tooltip = ({tt}: {tt: TooltipState | null}) => (
  <div
    className={`ta-tt${tt ? ' on' : ''}`}
    style={{
      left: `${Math.min(86, Math.max(13, tt?.leftPct ?? 50))}%`,
      top: tt?.topPx ?? 0,
    }}
    role="status"
  >
    {tt?.content}
  </div>
);

const TTRow = ({color, label, value}: {color?: string; label: string; value: string | number}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 8, padding: '1.5px 0'}}>
    {color && (
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: 2.5,
          background: color,
          flex: 'none',
        }}
      />
    )}
    <span style={{color: '#6C6579'}}>{label}</span>
    <span style={{marginLeft: 'auto', fontWeight: 700}}>{value}</span>
  </div>
);

export interface StackedSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

export const StackedDailyChart = ({
  dayLabels,
  series,
  selected = null,
  onSelectDay,
}: {
  dayLabels: string[];
  series: StackedSeries[];
  selected?: number | null;
  onSelectDay?: (i: number | null) => void;
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const [tt, setTt] = useState<TooltipState | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const W = 720;
  const H = 240;
  const padL = 32;
  const padR = 8;
  const padT = 10;
  const padB = 26;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const baseY = padT + plotH;

  const n = dayLabels.length;
  const dailyTotals = dayLabels.map((_, i) => series.reduce((a, s) => a + (s.values[i] || 0), 0));
  const maxStack = Math.max(...dailyTotals, 1);
  const stepY = maxStack > 24 ? Math.ceil(maxStack / 40) * 10 : 5;
  const yMax = Math.max(stepY, Math.ceil(maxStack / stepY) * stepY);
  const yScale = (v: number) => (v / yMax) * plotH;
  const band = plotW / n;
  const barW = Math.min(26, band * 0.62);
  const labelEvery = n > 16 ? Math.ceil(n / 15) : 1;

  const gridLines: number[] = [];
  for (let v = 0; v <= yMax; v += stepY) gridLines.push(v);

  const showDay = (i: number) => {
    setHover(i);
    setTt({
      leftPct: ((padL + band * i + band / 2) / W) * 100,
      topPx: 16,
      content: (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            <span>{dayLabels[i]}</span>
            <span style={{color: '#6C6579', fontWeight: 600}}>{dailyTotals[i]} total</span>
          </div>
          {series
            .filter((s) => (s.values[i] || 0) > 0)
            .map((s) => (
              <TTRow key={s.key} color={s.color} label={s.label} value={s.values[i]} />
            ))}
          {dailyTotals[i] === 0 && <div style={{color: '#9A93A8'}}>No actions</div>}
          {onSelectDay && dailyTotals[i] > 0 && (
            <div
              style={{
                marginTop: 6,
                paddingTop: 5,
                borderTop: '1px solid #ECE7F1',
                color: '#9A93A8',
                fontSize: 11,
              }}
            >
              Click to see all events this day
            </div>
          )}
        </>
      ),
    });
  };

  return (
    <div ref={wrapRef} style={{position: 'relative'}}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{display: 'block', width: '100%', height: 'auto'}}
        role="img"
        aria-label="Stacked bar chart of daily actions by type"
      >
        {gridLines.map((v) => {
          const y = baseY - yScale(v);
          return (
            <g key={`grid-${v}`}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#ECE7F1" strokeWidth="1" opacity={v === 0 ? 1 : 0.6} />
              <text x={padL - 7} y={y + 3.5} textAnchor="end" fontSize="11" fill="#9A93A8">
                {v}
              </text>
            </g>
          );
        })}
        {dayLabels.map((label, i) => {
          const cx = padL + band * i + band / 2;
          let acc = 0;
          const segs = series.filter((s) => (s.values[i] || 0) > 0);
          const active = hover === i || selected === i;
          const dimmed = (hover !== null && hover !== i) || (hover === null && selected !== null && selected !== i);
          return (
            <g key={`day-${label}-${i}`}>
              <rect
                x={cx - band / 2 + 1}
                y={padT}
                width={band - 2}
                height={plotH}
                rx={4}
                fill="#AE3EC9"
                opacity={selected === i ? 0.1 : hover === i ? 0.07 : 0}
              />
              {segs.map((s, si) => {
                const val = s.values[i];
                const hgt = yScale(val);
                const y = baseY - yScale(acc) - hgt;
                acc += val;
                const isTop = si === segs.length - 1;
                return (
                  <rect
                    key={s.key}
                    className="ta-bar"
                    style={{animationDelay: `${i * 22}ms`}}
                    x={cx - barW / 2}
                    y={y}
                    width={barW}
                    height={Math.max(0.5, hgt - (isTop ? 0 : 2))}
                    rx={isTop ? Math.min(4, barW / 2) : 0}
                    fill={s.color}
                    opacity={dimmed ? 0.4 : 1}
                  />
                );
              })}
              {selected === i && (
                <line
                  x1={cx - band / 2 + 2}
                  y1={baseY + 2}
                  x2={cx + band / 2 - 2}
                  y2={baseY + 2}
                  stroke="#AE3EC9"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              )}
              {i % labelEvery === 0 && (
                <text
                  x={cx}
                  y={baseY + 16}
                  textAnchor="middle"
                  fontSize="11"
                  fill={active ? '#201A2C' : '#9A93A8'}
                  fontWeight={active ? 700 : 400}
                >
                  {label}
                </text>
              )}
              <rect
                x={cx - band / 2}
                y={padT}
                width={band}
                height={plotH + padB}
                fill="transparent"
                style={{cursor: onSelectDay ? 'pointer' : 'default'}}
                onMouseEnter={() => showDay(i)}
                onMouseLeave={() => {
                  setHover(null);
                  setTt(null);
                }}
                onClick={() => onSelectDay && onSelectDay(selected === i ? null : i)}
              />
            </g>
          );
        })}
      </svg>
      <Tooltip tt={tt} />
    </div>
  );
};

export const HBarChart = ({items, color = '#AE3EC9'}: {items: {label: string; value: number}[]; color?: string}) => {
  const [hover, setHover] = useState<number | null>(null);
  const [tt, setTt] = useState<TooltipState | null>(null);

  const W = 460;
  const rowH = 44;
  const padL = 130;
  const padR = 44;
  const padT = 6;
  const H = padT + rowH * Math.max(items.length, 1) + 6;
  const plotW = W - padL - padR;
  const max = Math.max(...items.map((r) => r.value), 1);
  const total = items.reduce((a, r) => a + r.value, 0);

  return (
    <div style={{position: 'relative'}}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{display: 'block', width: '100%', height: 'auto'}}
        role="img"
        aria-label="Horizontal bar chart of actions by record type"
      >
        {items.map((r, i) => {
          const cy = padT + rowH * i + rowH / 2;
          const barH = 18;
          const bw = Math.max(barH, (r.value / max) * plotW);
          return (
            <g key={r.label}>
              <text
                x={padL - 12}
                y={cy + 4}
                textAnchor="end"
                fontSize="12.5"
                fill={hover === i ? '#201A2C' : '#6C6579'}
                fontWeight={hover === i ? 700 : 400}
              >
                {r.label}
              </text>
              <rect x={padL} y={cy - barH / 2} width={plotW} height={barH} rx={barH / 2} fill="#F1EDF6" />
              <rect
                className="ta-hbar"
                style={{animationDelay: `${i * 55}ms`}}
                x={padL}
                y={cy - barH / 2}
                width={bw}
                height={barH}
                rx={barH / 2}
                fill={hover === i ? '#8F27A8' : color}
              />
              <text
                className="ta-fadeup"
                style={{animationDelay: `${200 + i * 55}ms`}}
                x={padL + bw + 8}
                y={cy + 4}
                fontSize="12"
                fontWeight="600"
                fill="#201A2C"
              >
                {r.value}
              </text>
              <rect
                x={0}
                y={padT + rowH * i}
                width={W}
                height={rowH}
                fill="transparent"
                style={{cursor: 'pointer'}}
                onMouseEnter={() => {
                  setHover(i);
                  setTt({
                    leftPct: ((padL + bw / 2) / W) * 100,
                    topPx: 4 + (padT + rowH * i + rowH) * (100 / H),
                    content: (
                      <>
                        <div style={{fontWeight: 700, marginBottom: 5}}>{r.label}</div>
                        <TTRow color={color} label="Actions" value={r.value} />
                        <TTRow label="Share" value={`${Math.round((r.value / total) * 100)}%`} />
                      </>
                    ),
                  });
                }}
                onMouseLeave={() => {
                  setHover(null);
                  setTt(null);
                }}
              />
            </g>
          );
        })}
      </svg>
      <Tooltip tt={tt} />
    </div>
  );
};

export const MixBar = ({
  parts,
  height = 10,
  selectedKey = null,
  onSelect,
}: {
  parts: {label: string; value: number; color: string; key?: string}[];
  height?: number;
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
}) => {
  const total = parts.reduce((a, p) => a + p.value, 0);
  if (!total)
    return (
      <div
        style={{
          height,
          borderRadius: height / 2,
          background: '#F1EDF6',
          minWidth: 120,
        }}
      />
    );
  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        height,
        minWidth: 120,
        borderRadius: height / 2,
        overflow: 'hidden',
      }}
    >
      {parts
        .filter((p) => p.value > 0)
        .map((p, i) => {
          const key = p.key ?? p.label;
          const selected = selectedKey === key;
          return (
            <div
              key={key}
              className="ta-mixseg"
              title={onSelect ? `${p.label} · ${p.value} — click to ${selected ? 'clear' : 'focus'}` : `${p.label} · ${p.value}`}
              onClick={
                onSelect
                  ? (e) => {
                      e.stopPropagation();
                      onSelect(selected ? null : key);
                    }
                  : undefined
              }
              style={{
                width: `${(p.value / total) * 100}%`,
                minWidth: 3,
                background: p.color,
                borderRadius: 2.5,
                animationDelay: `${i * 45}ms`,
                opacity: selectedKey !== null && !selected ? 0.25 : 1,
                cursor: onSelect ? 'pointer' : undefined,
                transition: 'opacity .15s ease',
              }}
            />
          );
        })}
    </div>
  );
};

export const Legend = ({
  items,
  selectedKey = null,
  onSelect,
}: {
  items: {label: string; color: string; key?: string}[];
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
}) => (
  <div style={{display: 'flex', flexWrap: 'wrap', gap: onSelect ? '6px 6px' : '8px 14px'}}>
    {items.map((it) => {
      const key = it.key ?? it.label;
      const selected = selectedKey === key;
      const swatch = (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 3,
            background: it.color,
            flex: 'none',
          }}
        />
      );
      if (!onSelect)
        return (
          <span
            key={key}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#6C6579',
            }}
          >
            {swatch}
            {it.label}
          </span>
        );
      return (
        <button
          key={key}
          type="button"
          onClick={() => onSelect(selected ? null : key)}
          aria-pressed={selected}
          title={selected ? 'Clear filter' : `Only show ${it.label.toLowerCase()}`}
          className="ta-legend-btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            fontWeight: selected ? 700 : 400,
            color: selected ? '#201A2C' : '#6C6579',
            opacity: selectedKey !== null && !selected ? 0.45 : 1,
            padding: '3px 10px',
            borderRadius: 999,
            border: `1px solid ${selected ? it.color : 'transparent'}`,
            background: selected ? `${it.color}14` : 'transparent',
            cursor: 'pointer',
          }}
        >
          {swatch}
          {it.label}
        </button>
      );
    })}
  </div>
);

export const DeltaPill = ({current, prev}: {current: number; prev: number}) => {
  if (!prev) {
    return (
      <span
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          padding: '2px 8px',
          borderRadius: 999,
          background: '#F1EDF6',
          color: '#6C6579',
          whiteSpace: 'nowrap',
        }}
        title="No activity in the prior period"
      >
        —
      </span>
    );
  }
  const pct = Math.round(((current - prev) / prev) * 100);
  const up = pct >= 0;
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 999,
        background: up ? '#E7F5EA' : '#FCEAEA',
        color: up ? '#2F9E44' : '#E03131',
        whiteSpace: 'nowrap',
      }}
      title="vs. the prior period of the same length"
    >
      {up ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
};
